import React, {useContext, useEffect, useState} from 'react';
import {Card, CardBody, CardHeader, Col, Container, Row} from 'reactstrap';
import {Breadcrumbs, H5} from '../../../../AbstractElements';
import DataTable from "react-data-table-component";
import axiosConfig from "../../../../Config/axios";
import IconBtn from "../../../../CommonElements/IconButton";
import ButtonIcon from "../../../../CommonElements/ButtonIcon";
import Loader from "../../../../Layout/Loader";
import SearchOptions from "../../../CommonComponents/Search";
import {getPrivilegeRoute} from "../../../../Route/RouteIndex";
import AuthorizationContext from "../../../../_helper/Authorization";
import type {Permission} from "../Types/PermissionTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";

const PermissionIndex = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [searchVisibility, setSearchVisibility] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const searchOptions = [{name: 'name', label: 'Key'}, {name: 'display_name', label: 'Name'}, {name: 'description'}];
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const getPermissions = (params: any, page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};
        axiosConfig
            .get(getPrivilegeRoute('permission'), config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setPermissions(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const syncPermissions = () => {
        setLoaderVisibility(true);
        const config = {};
        axiosConfig
            .post(`/privilege/sync/permissions`, config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setPermissions(response.data.data.data);
                } else if (response?.data?.data) {
                    setPermissions(response.data.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    useEffect(() => {
        getPermissions({}, 1, perPage);
    }, [])

    const permissionTableColumns = [{
        name: 'Key', selector: (row: Permission) => row.name, sortable: false,
    }, {
        name: 'Name', selector: (row: Permission) => row.display_name, sortable: true, center: true,
    }, {
        name: 'Description', selector: (row: Permission) => row.description, sortable: true, center: true,
    }, {
        name: 'Option', selector: (row: Permission) => <div className='d-flex align-items-right gap-2'>
            <IconBtn link={`${getPrivilegeRoute('permission')}/${row.id}`} type={'view'} permission={'read-permission'}/>
        </div>, sortable: false, right: true,
    }];

    return (
        <div>
            <Breadcrumbs mainTitle="Permissions" parent="Privilege" title="Permissions"/>
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <Loader visibility={loaderVisibility}/>
                            <CardHeader className='d-flex align-items-center justify-content-between'>
                                <H5>Permissions</H5>
                                <div>
                                    <div className='d-flex align-items-center gap-2'>
                                        <ButtonIcon attrBtn={{
                                            onClick: syncPermissions
                                        }} type={'sync'} permission={['create-permission', 'update-permission']}/>
                                        <ButtonIcon attrBtn={{
                                            onClick: () => setSearchVisibility(!searchVisibility)
                                        }} type={'search'} permission={'read-permission'}/>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {searchVisibility && <SearchOptions callBack={(params: any) => getPermissions(params, 1, perPage)}
                                                                    searchOptions={searchOptions}/>}

                                <DataTable pagination paginationServer columns={permissionTableColumns}
                                           data={permissions}
                                           paginationTotalRows={totalRows}
                                           paginationPerPage={perPage}
                                           paginationDefaultPage={currentPage}
                                           onChangePage={(page) => getPermissions({}, page, perPage)}
                                           onChangeRowsPerPage={(newPerPage, page) => getPermissions({}, page, newPerPage)}
                                           highlightOnHover={true}
                                           striped={true} responsive={true}/>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default PermissionIndex;
